import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import CategoryIcon from '@mui/icons-material/Category';
import HandymanIcon from '@mui/icons-material/Handyman';
import { getAll, getById, downloadExcel, deleteLineItem, deleteAccessoryLineItem, deleteEstimation } from '../api/estimationApi';
import { useSnackbar } from '../context/SnackbarContext';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PageHeader from '../components/common/PageHeader';
import ConfirmDialog from '../components/common/ConfirmDialog';

const fmt = (v) => `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

export default function EstimationHistory() {
  const [estimations, setEstimations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewItem, setViewItem] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [deleteLineTarget, setDeleteLineTarget] = useState(null);     // { estimationId, itemId, name, type }
  const [deleteEstTarget, setDeleteEstTarget] = useState(null);
  const { showSnackbar } = useSnackbar();
  const { hasPermission } = useAuth();
  const canDeleteEstimation = hasPermission('DELETE_ESTIMATIONS');

  const load = useCallback(async () => {
    setLoading(true);
    try { setEstimations(await getAll()); }
    catch (e) { showSnackbar(e.message, 'error'); }
    finally { setLoading(false); }
  }, [showSnackbar]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (est) => {
    setDetailLoading(true);
    setViewItem({ id: est.id, title: est.title, loading: true });
    try {
      const detail = await getById(est.id);
      setViewItem(detail);
    } catch (e) { showSnackbar(e.message, 'error'); setViewItem(null); }
    finally { setDetailLoading(false); }
  };

  const handleDownloadExcel = async (id) => {
    try {
      await downloadExcel(id);
      showSnackbar('Estimation downloaded as Excel', 'success');
    } catch (e) { showSnackbar(e.message || 'Download failed', 'error'); }
  };

  const refreshDetail = async () => {
    if (viewItem?.id) {
      const detail = await getById(viewItem.id);
      setViewItem(detail);
    }
    load();
  };

  const handleDeleteLineItem = async () => {
    if (!deleteLineTarget) return;
    try {
      if (deleteLineTarget.type === 'accessory') {
        await deleteAccessoryLineItem(deleteLineTarget.estimationId, deleteLineTarget.itemId);
      } else {
        await deleteLineItem(deleteLineTarget.estimationId, deleteLineTarget.itemId);
      }
      showSnackbar('Item removed.', 'info');
      setDeleteLineTarget(null);
      await refreshDetail();
    } catch (e) { showSnackbar(e.message, 'error'); setDeleteLineTarget(null); }
  };

  const handleDeleteEstimation = async () => {
    if (!deleteEstTarget) return;
    try {
      await deleteEstimation(deleteEstTarget.id);
      showSnackbar(`"${deleteEstTarget.title}" deleted.`, 'success');
      setDeleteEstTarget(null);
      setViewItem(null);
      load();
    } catch (e) { showSnackbar(e.message, 'error'); setDeleteEstTarget(null); }
  };

  if (loading) return <LoadingSpinner />;

  const totalItems = (est) => {
    const mat = est.itemCount || 0;
    const acc = est.accessoryItemCount || 0;
    return mat + acc;
  };

  return (
    <Box>
      <PageHeader title="Estimation History" subtitle={`${estimations.length} estimation(s) on record`} />
      <Card>
        <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell align="center">Items</TableCell>
                <TableCell align="right">Total Value</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {estimations.map((est, i) => (
                <TableRow key={est.id} hover>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{est.title}</TableCell>
                  <TableCell>
                    {est.createdBy ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PersonIcon sx={{ fontSize: 13, color: '#757575' }} />
                        <Typography variant="body2">{est.createdBy}</Typography>
                      </Box>
                    ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      {est.itemCount > 0 && (
                        <Tooltip title="Material items">
                          <Chip icon={<CategoryIcon sx={{ fontSize: 12 }} />} label={est.itemCount} size="small" />
                        </Tooltip>
                      )}
                      {est.accessoryItemCount > 0 && (
                        <Tooltip title="Accessory items">
                          <Chip icon={<HandymanIcon sx={{ fontSize: 12 }} />} label={est.accessoryItemCount} size="small" color="secondary" />
                        </Tooltip>
                      )}
                      {totalItems(est) === 0 && <Chip label="0" size="small" />}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: '#1565C0' }}>{fmt(est.totalValue)}</TableCell>
                  <TableCell sx={{ color: '#757575', fontSize: 12 }}>{new Date(est.createdAt).toLocaleString('en-IN')}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="View details">
                      <IconButton size="small" color="primary" onClick={() => openDetail(est)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download as Excel">
                      <IconButton size="small" color="success" onClick={() => handleDownloadExcel(est.id)}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canDeleteEstimation && (
                      <Tooltip title="Delete estimation">
                        <IconButton size="small" color="error" onClick={() => setDeleteEstTarget(est)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {estimations.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: '#9e9e9e' }}>No estimations yet. Create one from New Estimation.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!viewItem} onClose={() => setViewItem(null)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              {viewItem?.title}
              <Typography variant="caption" display="block" color="text.secondary">
                Created: {viewItem?.createdAt && new Date(viewItem.createdAt).toLocaleString('en-IN')}
                {viewItem?.createdBy && ` · By: ${viewItem.createdBy}`}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, ml: 2, mt: 0.5 }}>
              {viewItem?.id && (
                <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => handleDownloadExcel(viewItem.id)}>
                  Download Excel
                </Button>
              )}
              {viewItem?.id && canDeleteEstimation && (
                <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => setDeleteEstTarget(viewItem)}>
                  Delete
                </Button>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailLoading ? <LoadingSpinner /> : viewItem?.lineItems != null ? (
            <>
              {/* Materials Section */}
              {viewItem.lineItems.length > 0 && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 0.5 }}>
                    <CategoryIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle2" fontWeight={700} color="primary.dark">Materials</Typography>
                    <Chip label={viewItem.lineItems.length} size="small" color="primary" />
                    {viewItem.materialTotal != null && <Chip label={fmt(viewItem.materialTotal)} size="small" color="primary" variant="outlined" />}
                  </Box>
                  <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#E3F2FD' }}>
                          <TableCell>#</TableCell>
                          <TableCell>Department</TableCell>
                          <TableCell>Group</TableCell>
                          <TableCell>Material</TableCell>
                          <TableCell>Unit</TableCell>
                          <TableCell>Make</TableCell>
                          <TableCell align="right">Unit Price</TableCell>
                          <TableCell align="right">Qty</TableCell>
                          <TableCell align="right">Total</TableCell>
                          <TableCell sx={{ width: 44 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {viewItem.lineItems.map((item, i) => (
                          <TableRow key={item.id} hover>
                            <TableCell>{i + 1}</TableCell>
                            <TableCell>{item.departmentName}</TableCell>
                            <TableCell sx={{ color: '#1565C0' }}>{item.materialGroupCode}</TableCell>
                            <TableCell sx={{ fontWeight: 500 }}>{item.materialName}</TableCell>
                            <TableCell><Chip label={item.unit} size="small" variant="outlined" /></TableCell>
                            <TableCell>{item.makeName}</TableCell>
                            <TableCell align="right">{fmt(item.unitPrice)}</TableCell>
                            <TableCell align="right">{item.quantity}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(item.totalPrice)}</TableCell>
                            <TableCell>
                              <Tooltip title="Remove this item">
                                <IconButton size="small" color="error"
                                  onClick={() => setDeleteLineTarget({ estimationId: viewItem.id, itemId: item.id, name: `${item.materialName} / ${item.makeName}`, type: 'material' })}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </>
              )}

              {/* Accessories Section */}
              {viewItem.accessoryItems?.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 1.5 }}>
                    <HandymanIcon sx={{ color: '#7B1FA2', fontSize: 18 }} />
                    <Typography variant="subtitle2" fontWeight={700} color="secondary.dark">Accessories</Typography>
                    <Chip label={viewItem.accessoryItems.length} size="small" color="secondary" />
                    {viewItem.accessoryTotal != null && <Chip label={fmt(viewItem.accessoryTotal)} size="small" color="secondary" variant="outlined" />}
                  </Box>
                  <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#F3E5FF' }}>
                          <TableCell>#</TableCell>
                          <TableCell>Department</TableCell>
                          <TableCell>Group</TableCell>
                          <TableCell>Accessory</TableCell>
                          <TableCell>Unit</TableCell>
                          <TableCell>Make</TableCell>
                          <TableCell align="right">Unit Price</TableCell>
                          <TableCell align="right">Qty</TableCell>
                          <TableCell align="right">Total</TableCell>
                          <TableCell sx={{ width: 44 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {viewItem.accessoryItems.map((item, i) => (
                          <TableRow key={item.id} hover>
                            <TableCell>{i + 1}</TableCell>
                            <TableCell>{item.departmentName}</TableCell>
                            <TableCell sx={{ color: '#7B1FA2' }}>{item.accessoryGroupCode}</TableCell>
                            <TableCell sx={{ fontWeight: 500 }}>{item.accessoryName}</TableCell>
                            <TableCell><Chip label={item.unit} size="small" variant="outlined" /></TableCell>
                            <TableCell>{item.makeName}</TableCell>
                            <TableCell align="right">{fmt(item.unitPrice)}</TableCell>
                            <TableCell align="right">{item.quantity}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: '#7B1FA2' }}>{fmt(item.totalPrice)}</TableCell>
                            <TableCell>
                              <Tooltip title="Remove this item">
                                <IconButton size="small" color="error"
                                  onClick={() => setDeleteLineTarget({ estimationId: viewItem.id, itemId: item.id, name: `${item.accessoryName} / ${item.makeName}`, type: 'accessory' })}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </>
              )}

              {/* Grand total */}
              {(viewItem.lineItems.length > 0 || viewItem.accessoryItems?.length > 0) && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3, mt: 1, p: 1.5, bgcolor: '#E8F5E9', borderRadius: 1 }}>
                  {viewItem.lineItems.length > 0 && viewItem.accessoryItems?.length > 0 && (
                    <>
                      <Typography variant="body2" color="primary.dark">
                        Materials: <strong>{fmt(viewItem.materialTotal || 0)}</strong>
                      </Typography>
                      <Typography variant="body2" color="secondary.dark">
                        Accessories: <strong>{fmt(viewItem.accessoryTotal || 0)}</strong>
                      </Typography>
                      <Divider orientation="vertical" flexItem />
                    </>
                  )}
                  <Typography variant="h6" fontWeight={700} color="success.dark">
                    Grand Total: {fmt(viewItem.grandTotal)}
                  </Typography>
                </Box>
              )}

              {viewItem.lineItems.length === 0 && (!viewItem.accessoryItems || viewItem.accessoryItems.length === 0) && (
                <Alert severity="info">All items have been removed from this estimation.</Alert>
              )}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewItem(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete line item confirm */}
      <ConfirmDialog
        open={!!deleteLineTarget}
        title={`Remove ${deleteLineTarget?.type === 'accessory' ? 'Accessory' : 'Material'} Item`}
        message={`Remove "${deleteLineTarget?.name}" from this estimation? This cannot be undone.`}
        confirmLabel="Remove"
        onConfirm={handleDeleteLineItem}
        onCancel={() => setDeleteLineTarget(null)}
      />

      {/* Delete entire estimation confirm */}
      <ConfirmDialog
        open={!!deleteEstTarget}
        title="Delete Estimation"
        message={`Delete estimation "${deleteEstTarget?.title}"?\n\nThis will permanently remove the estimation and all its items. This cannot be undone.`}
        confirmLabel="Yes, Delete"
        confirmColor="error"
        onConfirm={handleDeleteEstimation}
        onCancel={() => setDeleteEstTarget(null)}
      />
    </Box>
  );
}
